import { fieldPositions, flightTimeScale, goalZones } from "@Context/game/builder/builder-context";
import { clamp, isEmpty, isNull, isNumber, isString, isUndefined, round } from "lodash-es";
import { buildBallStatesFromBallPoints, parseTrackedPossessorId } from "@utilities/scenario-builder-replay";

import { ScenarioActionType } from "@Enums/index";
import { PitchData, ScenarioAction, ScenarioBallFrame, ScenarioBallSegment, ScenarioData, ScenarioWaypoint } from "@Shared/types/ScenarioTypes";
import {
  BallPoint,
  BallState,
  BuilderState,
  FieldSpace,
  Player,
  PlayerAction,
  ScenarioClip,
  ScenarioState,
  StateChangeSource,
} from "../models/builder-data-types";
import { getExportPlayerName, getFieldPositionFromName, getFieldPositionFromNumber, getPlayer } from "./player-functions";
import { fieldToScreen, fieldToScreen3, getBeziers, getNearestPlayer, getPassFlightTime, getPointAtTime, screenToField } from "./utilities";

const playerIdMap = new Map<string, number>([
  ["1_1_Goalkeeper", -904152],
  ["1_2_Right_Back", -906228],
  ["1_3_Left_Back", -906920],
  ["1_4_Right_Centre_Back", -907612],
  ["1_5_Left_Centre_Back", -908304],
  ["1_6_Defensive_Midfielder", -908996],
  ["1_7_Right_Winger", -909688],
  ["1_8_Central_Midfielder", -910380],
  ["1_9_Striker", -911072],
  ["1_10_Attacking_Midfielder", -904844],
  ["1_11_Left_Winger", -905536],
  ["2_1_Goalkeeper", -911764],
  ["2_2_Right_Back", -913840],
  ["2_3_Left_Back", -914532],
  ["2_4_Right_Centre_Back", -915224],
  ["2_5_Left_Centre_Back", -915916],
  ["2_6_Defensive_Midfielder", -916608],
  ["2_7_Right_Winger", -917300],
  ["2_8_Central_Midfielder", -917992],
  ["2_9_Striker", -918684],
  ["2_10_Attacking_Midfielder", -912456],
  ["2_11_Left_Winger", -913148],
]);

const parseScenarioPlayerId = (playerId: unknown): number | null => {
  if (isNull(playerId) || isUndefined(playerId) || playerId === "") {
    return null;
  }

  const parsedPlayerId = isString(playerId) ? Number(playerId) : playerId;
  return typeof parsedPlayerId === "number" && Number.isFinite(parsedPlayerId) ? parsedPlayerId : null;
};

const getScenarioIdFromPlayerId = (playerId: number | null | undefined, clip: ScenarioClip) => {
  if (isNull(playerId) || isUndefined(playerId)) {
    return null;
  }

  if (!Number.isFinite(playerId)) {
    return null;
  }

  if (playerId === -1) {
    return -1;
  }

  const player = getPlayer(clip, playerId);
  if (!player) {
    console.warn("Player not found for id", playerId);
    return playerId;
  }
  const playerName = getExportPlayerName(player);
  return playerIdMap.get(playerName) ?? playerId;
};

const getScenarioStateFromData = (data: any, field: FieldSpace, ballSegments: ScenarioBallSegment[]): ScenarioState => {
  const newPlayers = new Map<number, Player>();
  for (let i = 0; i < data.Players.length; i++) {
    const player = data.Players[i];
    const parsedPlayerId = parseScenarioPlayerId(player.Hash) ?? parseScenarioPlayerId(player.id) ?? -(1000000 + i);
    const playerPoints = player.Route.Waypoints.map((point: ScenarioWaypoint) => {
      return {
        position: fieldToScreen(point.WorldPosition, field),
        prev: fieldToScreen(point.PreviousTangent, field),
        next: fieldToScreen(point.NextTangent, field),
        t: point.Time,
      };
    });

    const fieldPosition = player.JerseyNumber ? getFieldPositionFromNumber(player.JerseyNumber) : getFieldPositionFromName(player.PlayerName);

    const newPlayer = {
      name: fieldPosition ? fieldPosition.name : player.PlayerName ? player.PlayerName : `Player ${player.JerseyNumber}`,
      id: parsedPlayerId,
      initials: fieldPosition ? fieldPosition.initials : "?",
      jerseyNumber: player.JerseyNumber || fieldPosition?.jerseyNumber || -1,
      isPlayer: !!player.IsPlayer,
      team: player.Team,
      position: player.Position,
      points: playerPoints,
      actions: player.Route.Actions.filter((a: ScenarioAction) => a.enabled).map((action: ScenarioAction) => {
        return {
          type: action.type,
          startTime: action.startTime,
          endTime: action.endTime,
          data: action.data,
          index: action.index,
          targetPlayer: parseScenarioPlayerId(action.target),
          targetPosition: action.location && action.location.WorldPosition ? fieldToScreen(action.location.WorldPosition, field) : null,
          height: action.height,
          direction: action.direction,
          time: action.time,
          isGenerated: !isNull(action.isGenerated) && !isUndefined(action.isGenerated) ? action.isGenerated : false,
        };
      }),
      beziers: getBeziers(playerPoints),
    };

    newPlayers.set(newPlayer.id, newPlayer);
  }

  let foundIncorrectTeam = false;

  newPlayers.forEach(player => {
    if (player.team !== 1 && player.team !== 2) {
      foundIncorrectTeam = true;
      console.log("Found incorrect team for player", player.name, "with team", player.team);
    }
    player.actions.forEach((action: PlayerAction, index: number) => {
      if (action.type === ScenarioActionType.PassTarget && action.time <= action.startTime) {
        const kickerPos = getPointAtTime(player.points, action.startTime, player.beziers);
        const receiver = newPlayers.get(action.targetPlayer);
        if (receiver) {
          const receiverPos = getPointAtTime(receiver.points, action.startTime, receiver.beziers);
          action.time = action.startTime + getPassFlightTime(kickerPos.position, receiverPos.position, flightTimeScale);
          console.log("Patched missing pass time for " + player.name + " (team " + player.team + ") pass action (" + index + ")", action);
        } else {
          console.error("Receiver not found for " + player.name + " (team " + player.team + ") pass action (" + index + ")", action);
        }
      } else if (
        action.type === ScenarioActionType.ShootDirection &&
        (!action.targetPosition || isNaN(action.targetPosition.x) || isNaN(action.targetPosition.y))
      ) {
        action.targetPosition = goalZones[player.team - 1];
        console.log("Patched missing shoot target for " + player.name + " (team " + player.team + ") shoot action (" + index + ")", action);
      }
    });
  });

  if (foundIncorrectTeam) {
    newPlayers.forEach(player => {
      player.team += 1;
    });
    console.log("Patched incorrect teams for all players");
  }

  const trackedBallPoints =
    data.BallFrames?.map((frame: ScenarioBallFrame) => {
      return {
        position: fieldToScreen3(frame.Position, field),
        t: frame.Time,
        possessor: frame.Possessor,
      };
    }) || [];

  const trackedInitialPossessor = parseTrackedPossessorId(trackedBallPoints[0]?.possessor);

  return {
    players: newPlayers,
    ball: {
      initialPosition: fieldToScreen(data.InitialBallPos, field),
      initialPossessor: trackedInitialPossessor ?? getNearestPlayer(newPlayers, fieldToScreen(data.InitialBallPos, field), 0),
      points: trackedBallPoints,
      segments:
        ballSegments?.map(segment => {
          return {
            startPoint: fieldToScreen3(segment.startPoint, field),
            endPoint: fieldToScreen3(segment.endPoint, field),
            startTime: segment.startTime,
            endTime: segment.endTime,
            fromPlayer: segment.fromPlayer,
            toPlayer: segment.toPlayer,
            frames: segment.frames || [],
          };
        }) || [],
      debugPoints: data.DebugBallFrames?.map((frame: ScenarioBallFrame) => {
        return {
          position: fieldToScreen3(frame.Position, field),
          t: frame.Time,
          possessor: frame.Possessor,
        };
      }) || [],
      disabledPoints: data.DisabledBallFrames?.map((frame: ScenarioBallFrame) => {
        return {
          position: fieldToScreen3(frame.Position, field),
          t: frame.Time,
          possessor: frame.Possessor,
        };
      }) || [],
    },
    endTime: data.EndTime,
    fileName: data.Name,
    matchTime: data.MatchTime || 0,
    pitch: data.Pitch ? { ...data.Pitch } as PitchData : undefined
  };
};

const loadScenario = (
  scenarioState: ScenarioState,
  source: StateChangeSource,
  state: BuilderState,
  dispatch: (data: any) => void,
  isPreview = false,
  forceDirty = false
) => {
  const keys = Array.from(scenarioState.players.keys());
  const newIds = state.availableIds.filter(id => keys.findIndex(key => key == id) === -1);
  const allPlayers = Array.from(scenarioState.players.values());
  const allTeamMates = allPlayers.filter(player => player.team == 1);
  const allOpponents = allPlayers.filter(player => player.team == 2);
  const newTeamMates = fieldPositions.filter(fieldPosition => allTeamMates.findIndex(p => p.initials == fieldPosition.initials) === -1);
  const newOpponents = fieldPositions.filter(fieldPosition => allOpponents.findIndex(p => p.initials == fieldPosition.initials) === -1);

  const newData = {
    stateChangeSource: source,
    clip: {
      ...state.clip,
      isPreview,
      scenario: {
        ...state.clip.scenario,
        ...scenarioState,
      },
    },
    time: 0,
    availableTeamMates: newTeamMates,
    availableOpponents: newOpponents,
    availableIds: newIds,
    forceDirty,
    ballStates: (() => {
      const trackedBallStates = buildBallStatesFromBallPoints({
        ballPoints: scenarioState.ball.points,
        initialPossessor: scenarioState.ball.initialPossessor,
        initialPosition: scenarioState.ball.initialPosition,
      });

      if (trackedBallStates.length > 0) {
        return trackedBallStates;
      }

      return [{ state: "idle", time: 0, player: scenarioState.ball.initialPossessor, position: scenarioState.ball.initialPosition }];
    })(),
  };

  dispatch(newData);
};

const savePoint = (point: BallPoint, field: FieldSpace): ScenarioBallFrame => {
  return {
    Position: {
      x: screenToField(point.position, field).x,
      y: screenToField(point.position, field).y,
      z: point.position.z,
    },
    Time: point.t,
    Possessor: point.possessor,
    Speed: 0,
    Acceleration: 0,
    Direction: 0,
  };
};

const savePoints = (points: BallPoint[], field: FieldSpace): ScenarioBallFrame[] => {
  return points.map(point => savePoint(point, field)).filter(frame => isFinite(frame.Position.x) && isFinite(frame.Position.y) && isFinite(frame.Position.z));
};

const saveScenario = (clip: ScenarioClip, field: FieldSpace) => {

  const players = Array.from(clip.scenario.players.values());

  // If the ball points don't have possessor data, calculate them from the pass actions
  if (clip.scenario.ball.points && clip.scenario.ball.points.every(point => !point.possessor)) {
    let possessor = players.find(player => player.id == clip.scenario.ball.initialPossessor);
    let currentTime = 0;
    let currentPass: PlayerAction | undefined = possessor?.actions.find(action => action.type == ScenarioActionType.PassTarget);
    const possessionChain: BallState[] = [{ state: "possessed", time: 0, player: possessor?.id }];
    while (currentPass && currentTime < clip.scenario.endTime) {
      const passTargetPlayer = players.find(player => player.id == currentPass?.targetPlayer);
      if (passTargetPlayer) {
        possessionChain.push({
          state: "possessed",
          time: currentPass.time,
          player: passTargetPlayer.id,
          receiveTime: currentPass.time,
        });
        currentTime = currentPass?.endTime || clip.scenario.endTime;
        currentPass = passTargetPlayer.actions.find(action => action.type == ScenarioActionType.PassTarget && action.time >= currentTime);
      } else {
        currentPass = undefined;
      }
    }

    for (let i = 0; i < clip.scenario.ball.points.length; i++) {
      const point = clip.scenario.ball.points[i];
      const fromPossession = possessionChain.findLast(state => state.time <= point.t);
      const toPossession = possessionChain.find(state => state.time > point.t);
      point.possessor = fromPossession?.player?.toString() || toPossession?.player?.toString() || undefined;
    }
  }

  const data: ScenarioData = {
    Name: clip.scenario.fileName,
    EditorVersion: "2.99.99",
    EndTime: clip.scenario.endTime,
    InitialBallPos: screenToField(clip.scenario.ball.initialPosition, field),
    Players: players
      .map(player => {
        // HOTFIX: This is to homogenise player ID to circumvent issue in VR, remove when fixed (can just user player.id)
        const playerId = getScenarioIdFromPlayerId(player.id, clip) ?? player.id;

        return {
          PlayerName: getExportPlayerName(player),
          JerseyNumber: player.jerseyNumber,
          id: playerId + "", //not a typo: id is lowercase in the JSON
          Hash: playerId,
          IsPlayer: !!player.isPlayer,
          Team: player.team,
          Position: player.position,
          Route: {
            Waypoints: player.points.map(point => {
              return {
                WorldPosition: screenToField(point.position, field),
                PreviousTangent: screenToField(point.prev, field),
                NextTangent: screenToField(point.next, field),
                Time: point.t,
              };
            }),
            Actions: player.actions.map((action: PlayerAction) => {
              const targetPlayerId = getScenarioIdFromPlayerId(action.targetPlayer, clip);

              return {
                enabled: true,
                type: action.type,
                startTime: action.startTime,
                endTime: action.endTime,
                target: targetPlayerId,
                location: action.targetPosition ? { WorldPosition: screenToField(action.targetPosition, field) } : null,
                data: action.data,
                index: action.index,
                height: action.height,
                direction: action.direction,
                time: action.time,
                isGenerated: action.isGenerated,
              };
            }),
          },
        };
      })
      .sort((a, b) => a.PlayerName.localeCompare(b.PlayerName)),
    BallFrames: savePoints(clip.scenario.ball.points, field),
    DebugBallFrames: savePoints(clip.scenario.ball.debugPoints, field),
    DisabledBallFrames: savePoints(clip.scenario.ball.disabledPoints, field),
    Pitch: clip.scenario.pitch ? { ...clip.scenario.pitch } as PitchData : undefined
  };
  return data;
};

const generateActions = (player: Player, newPlayers: Map<number, Player>, state: BuilderState) => {
  // If we have imported events from tracking data, use them to generate score actions
  if (!isEmpty(state.importEvents)) {
    const scenarioEvents = state.importEvents.filter(e => {
      const eventTime = e.timeMin * 60 + e.timeSec;
      return eventTime >= state.importRange[0] && eventTime <= state.importRange[1];
    });

    const passEvents = scenarioEvents.filter(e => e.typeId === 1);
    const playerPassEvents = passEvents.filter(e => e.playerId === player.name);

    playerPassEvents.forEach(event => {
      if (event.passOption) {
        event.passOption.player?.forEach((passOption: any) => {
          const passOptionPlayer = Array.from(newPlayers.values()).find(p => p.name === passOption.playerId);
          if (passOptionPlayer) {
            const eventTime = calculateEventTime(event, state);
            const optionScore = clamp(round(passOption.predictions?.passOptionQuality?.value || 0, 1), 0, 1) * 100;
            const newScoreAction = {
              type: ScenarioActionType.Score,
              startTime: clamp(eventTime, 0, state.clip.scenario.endTime),
              endTime: clamp(eventTime + 1, 0, state.clip.scenario.endTime),
              targetPlayer: -1,
              targetPosition: { x: 0, y: 0 },
              data: {},
              index: optionScore,
              height: 0,
              direction: 0,
              time: 1,
              isGenerated: true,
            };

            passOptionPlayer.actions.push(newScoreAction);

            newPlayers.set(passOptionPlayer.id, passOptionPlayer);
          }
        });
      }
    });

    // Auto generate a tackler to pressure the player
    // Calculate when the player receives the ball
    const passToPlayer = passEvents.find(e => e.reception?.player?.playerId === player.name || e.passTarget?.player[0]?.playerId === player.name);
    if (passToPlayer) {
      const receiveTime = calculateEventTime(passToPlayer, state);
      // Find the closest opponent to the player some time after their receive the ball
      const playerPosition = getPointAtTime(player.points, receiveTime + 4, player.beziers);
      const opponents = Array.from(newPlayers.values()).filter(p => p.team !== player.team);
      const opponentMap = new Map<number, Player>();
      opponents.forEach(opponent => opponentMap.set(opponent.id, opponent));
      const closestOpponentKey = getNearestPlayer(opponentMap, playerPosition.position, receiveTime + 4);
      if (isNumber(closestOpponentKey) && closestOpponentKey !== -1) {
        const closestOpponent = newPlayers.get(closestOpponentKey);

        // Add GetBall action to closest opponent
        if (closestOpponent) {
          const newGetBallAction = {
            type: ScenarioActionType.GetBall,
            startTime: clamp(receiveTime + 4, 0, state.clip.scenario.endTime),
            endTime: clamp(receiveTime + 10, 0, state.clip.scenario.endTime),
            targetPlayer: player.id,
            targetPosition: playerPosition.position,
            data: {},
            index: 0,
            height: 0,
            direction: 0,
            time: 1,
            isGenerated: true,
          };

          closestOpponent.actions.push(newGetBallAction);

          newPlayers.set(closestOpponentKey, closestOpponent);
        }
      }
    }
  }
};

export { generateActions, getScenarioStateFromData, loadScenario, saveScenario };
